import { _assert, _first } from '@naturalcycles/js-lib';
/**
 * The price class uses cents so that we only have to deal with integers and avoid javascript amazing maths skills with floats
 * Also, in the real world, prices never have less than 1 cents
 */
export class ExperimentalPrice {
    amount;
    currency;
    constructor(amount, currency) {
        this.amount = amount;
        this.currency = currency;
        _assert(Number.isInteger(amount), `Amount must be an integer: ${amount}`);
    }
    /**
     * Represent the cents as a float number
     * e.g 999 cents = 9.99
     */
    get units() {
        return this.amount / 100;
    }
    /**
     * @param str can be in the format of `100.99[USD]`. Where 100.99 is NOT in cents
     */
    static fromString(str) {
        const parsed = str.match(PRICE_VALIDATION_REGEX);
        _assert(parsed, `Invalid ExperimentalPrice string: ${str}`);
        const cents = Math.round(Number(parsed[1]) * 100);
        const currency = parsed[3];
        _assert(isCurrency(currency), `Invalid currency: ${currency}`);
        return new ExperimentalPrice(cents, currency);
    }
    static fromObject(priceObject) {
        return new ExperimentalPrice(priceObject.amount, priceObject.currency);
    }
    static fromCents(cents, currency) {
        return new ExperimentalPrice(cents, currency);
    }
    static fromUnitsString(units, currency) {
        const cents = ExperimentalPrice.unitsStringToCents(units);
        return new ExperimentalPrice(cents, currency);
    }
    /**
     * Returns cents value from inputs where units is a float number
     * e.g 9.99 / 9,99 / 10.00
     */
    static unitsStringToCents(units) {
        const formatted = units.replace(',', '.');
        _assert(FORMATTED_FLOAT_NUMBER_REGEX.test(formatted), 'Invalid units string: ' + units);
        const asNumber = parseFloat(formatted);
        return Math.round(asNumber * 100);
    }
    static centsToUnits(cents) {
        return cents / 100;
    }
    static centsToUnitsString(cents) {
        return ExperimentalPrice.centsToUnits(cents).toFixed(2);
    }
    /**
     * This sums the prices of all the inputs and output a total price
     * It assumes there is at least one price input and will throw otherwise
     * It will also throw if the currencies do not match
     */
    static sumPrices(prices) {
        const { currency } = _first(prices);
        let sum = ExperimentalPrice.fromCents(ZERO, currency);
        prices.forEach(price => {
            sum = sum.plusPrice(price);
        });
        return sum;
    }
    toString() {
        return `${this.units}[${this.currency}]`;
    }
    toLocaleString(locale) {
        const hasFraction = !!(this.units % 1);
        const language = locale.slice(0, 2);
        const formattedString = this.units.toLocaleString(language, {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: hasFraction ? 2 : 0,
            maximumFractionDigits: hasFraction ? 2 : 0,
        });
        const isFloatNumber = FORMATTED_FLOAT_NUMBER_REGEX.test(formattedString);
        if (isFloatNumber) {
            const fallbackString = this.toString().replaceAll(SQUARE_BRACKET_REGEX, ' ').trim();
            return fallbackString;
        }
        return formattedString;
    }
    toJSON() {
        return this.toString();
    }
    /**
     * Converts the price to a float number representing the units.
     * e.g 999 cents = 9.99
     */
    toUnits() {
        return this.units;
    }
    /**
     * Converts the price to a string representing the units.
     * e.g 999 cents = "9.99"
     */
    toUnitsString() {
        return this.units.toFixed(2);
    }
    // Manipulation (non-mutating)
    plus(amount) {
        return new ExperimentalPrice((this.amount + amount), this.currency);
    }
    minus(amount) {
        return new ExperimentalPrice((this.amount - amount), this.currency);
    }
    multiply(multiplier) {
        return new ExperimentalPrice(Math.round(this.amount * multiplier), this.currency);
    }
    divide(divider) {
        _assert(divider !== 0, 'Cannot divide by 0');
        return new ExperimentalPrice(Math.round(this.amount / divider), this.currency);
    }
    plusPrice(price) {
        this.assertSameCurrency(price);
        return this.plus(price.amount);
    }
    minusPrice(price) {
        this.assertSameCurrency(price);
        return this.minus(price.amount);
    }
    /**
     * Accepts a discount percentage and returns a new price with the discount applied.
     * It's expected that the discount percentage can be a float number.
     */
    withDiscount(discountPercentage) {
        return this.multiply(1 - discountPercentage / 100);
    }
    // Comparison
    isLessThan(price) {
        this.assertSameCurrency(price);
        return this.amount < price.amount;
    }
    isSameOrLessThan(price) {
        this.assertSameCurrency(price);
        return this.amount <= price.amount;
    }
    isMoreThan(price) {
        this.assertSameCurrency(price);
        return this.amount > price.amount;
    }
    isSameOrMoreThan(price) {
        this.assertSameCurrency(price);
        return this.amount >= price.amount;
    }
    isSameAs(price) {
        this.assertSameCurrency(price);
        return this.amount === price.amount;
    }
    assertSameCurrency(price2) {
        _assert(this.currency === price2.currency, `Currencies do not match: ${this.currency} !== ${price2.currency}`);
    }
    /**
     * Returns the discount amount in cents.
     * e.g If the price is $100.00 (10000 cents) and the discount is 19%, it will return 1900.
     * Its expected that the discount percentage can be a float number.
     */
    static getDiscountAmount(amount, discountPercentage) {
        const discountAmount = (amount * discountPercentage) / 100;
        return Math.round(discountAmount);
    }
}
export function isCurrency(currency) {
    return CURRENCY_VALIDATION_REGEX.test(currency);
}
export const CURRENCY_VALIDATION_REGEX = /^[A-Z]{3}$/;
const PRICE_VALIDATION_REGEX = /^(-?\d+(\.\d+)?)\[([A-Z]{3})]$/;
const SQUARE_BRACKET_REGEX = /(\[|])/;
const FORMATTED_FLOAT_NUMBER_REGEX = /^-?[0-9,.]+$/;
export const ZERO = 0;
export const AUD = 'AUD';
export const CAD = 'CAD';
export const CHF = 'CHF';
export const DKK = 'DKK';
export const EUR = 'EUR';
export const GBP = 'GBP';
export const NOK = 'NOK';
export const SEK = 'SEK';
export const SGD = 'SGD';
export const USD = 'USD';
